import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { AuthContext } from '../context/AuthContext';
import { usePlatform, useRunTimeEnvironment, useScript } from '../utils/hooks';
import { runAdmob, useAdsListener } from '../services/admob';

const PENDING_STORAGE_KEY = 'pedometer-pending-claim';
const PENDING_TTL_MS = 180000;
const ERROR_GRACE_MS = 400;
const AD_HANDOFF_MS = 900;

function ensureNotifyAds() {
  if (typeof window.notifyAds === 'function') {
    return;
  }
  window.notifyAds = (event, reward) => {
    window.dispatchEvent(
      new CustomEvent('ads', {
        detail: { event, reward },
      }),
    );
  };
}

function writePending(pending) {
  try {
    sessionStorage.setItem(PENDING_STORAGE_KEY, JSON.stringify({ ...pending, ts: Date.now() }));
  } catch (error) {
    // private mode 등
  }
}

function clearStoredPending() {
  try {
    sessionStorage.removeItem(PENDING_STORAGE_KEY);
  } catch (error) {
    // no-op
  }
}

function readStoredPending() {
  try {
    const raw = sessionStorage.getItem(PENDING_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw);
    if (!parsed?.code) {
      return null;
    }
    if (Date.now() - (parsed.ts || 0) > PENDING_TTL_MS) {
      sessionStorage.removeItem(PENDING_STORAGE_KEY);
      return null;
    }
    return { code: parsed.code, segmentNo: parsed.segmentNo };
  } catch (error) {
    return null;
  }
}

export function usePedometerClaim({ today, mutate, onToast }) {
  const RTE = useRunTimeEnvironment();
  const browserDeviceType = usePlatform();
  const { API_SERVER_DOMAIN, useAccessToken, appInfo } = useContext(AuthContext);
  const accessToken = useAccessToken();
  const deviceType = appInfo?.os === 'ios' || appInfo?.os === 'android' ? appInfo.os : browserDeviceType;
  const pendingRef = useRef(null);
  const errorTimerRef = useRef(null);
  const claimLockRef = useRef(false);
  const ignoreAdEventUntilRef = useRef(0);
  const adShownAtRef = useRef(0);
  const [busy, setBusy] = useState(false);
  const [claimBurst, setClaimBurst] = useState(null);
  const burstTimerRef = useRef(null);

  useScript(`${process.env.PUBLIC_URL}/flutter/flutter_script.js`);

  const segments = useMemo(() => (Array.isArray(today?.segments) ? today.segments : []), [today]);
  const steps = today?.steps ?? 0;

  const segmentByNo = useCallback(
    (segmentNo) => segments.find((item) => item?.segmentNo === segmentNo) ?? null,
    [segments],
  );

  useEffect(() => {
    ensureNotifyAds();
    const stored = readStoredPending();
    if (stored && !pendingRef.current) {
      pendingRef.current = stored;
      setBusy(true);
    }
  }, []);

  useEffect(() => {
    return () => {
      if (errorTimerRef.current) {
        window.clearTimeout(errorTimerRef.current);
      }
      if (burstTimerRef.current) {
        window.clearTimeout(burstTimerRef.current);
      }
    };
  }, []);

  const playClaimBurst = useCallback((json) => {
    const kind = Number(json?.recvSnack) > 0 || json?.toastKey === 'SNACK' ? 'snack' : 'point';
    const id = `${Date.now()}-${kind}`;
    setClaimBurst({ id, kind, segmentNo: json?.segmentNo });
    if (burstTimerRef.current) {
      window.clearTimeout(burstTimerRef.current);
    }
    // GIF 1.11초 1회 재생
    burstTimerRef.current = window.setTimeout(() => setClaimBurst(null), 1200);
  }, []);

  const setPending = useCallback((pending) => {
    pendingRef.current = pending;
    if (pending?.code) {
      writePending(pending);
    } else {
      clearStoredPending();
    }
  }, []);

  const takePending = useCallback(() => {
    const pending = pendingRef.current?.code ? pendingRef.current : readStoredPending();
    if (!pending?.code) {
      return null;
    }
    pendingRef.current = null;
    clearStoredPending();
    return pending;
  }, []);

  const postClaim = useCallback(
    async (body) => {
      const response = await fetch(`${API_SERVER_DOMAIN}/main/pedometer/claim`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });
      return response.json();
    },
    [API_SERVER_DOMAIN, accessToken],
  );

  const handleAdError = useCallback(
    (errorInfo = {}) => {
      if (Date.now() < ignoreAdEventUntilRef.current) {
        return;
      }
      if (!pendingRef.current?.code && !readStoredPending()) {
        return;
      }
      const pendingAtError = pendingRef.current?.code || readStoredPending()?.code;
      if (errorTimerRef.current) {
        window.clearTimeout(errorTimerRef.current);
      }
      errorTimerRef.current = window.setTimeout(() => {
        const currentCode = pendingRef.current?.code || readStoredPending()?.code;
        if (!currentCode || currentCode !== pendingAtError) {
          return;
        }
        const watchedMs = Date.now() - (adShownAtRef.current || 0);
        if (watchedMs < 3500) {
          setPending(null);
          setBusy(false);
          const message =
            typeof errorInfo?.message === 'string' && errorInfo.message.trim().length > 0
              ? errorInfo.message
              : '광고를 끝까지 봐야 보상이 지급됩니다.';
          onToast?.(message);
          return;
        }
        onAdEndRef.current();
      }, ERROR_GRACE_MS);
    },
    [onToast, setPending],
  );

  const startAd = useCallback(async () => {
    if (RTE !== 'APP') {
      onToast?.('광고 시청은 앱에서만 가능해요');
      return false;
    }
    if (typeof window.flutter_inappwebview?.callHandler !== 'function') {
      onToast?.('광고 재생에 실패했어요.');
      return false;
    }
    ensureNotifyAds();
    setBusy(true);
    try {
      const response = await fetch(`${API_SERVER_DOMAIN}/main/pedometer/ad/start`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ deviceType }),
      });
      const json = await response.json();
      if (json?.result !== 'ok') {
        onToast?.(json?.message || '광고를 불러오지 못했어요.');
        setBusy(false);
        return false;
      }
      setPending({ code: json.code, segmentNo: json.segmentNo });
      ignoreAdEventUntilRef.current = Date.now() + AD_HANDOFF_MS;
      adShownAtRef.current = Date.now();
      runAdmob(json.adType ?? '', json.placementId ?? '');
      return true;
    } catch (error) {
      console.error('pedometer ad start error', error);
      setPending(null);
      onToast?.('광고를 불러오지 못했어요.');
      setBusy(false);
      return false;
    }
  }, [API_SERVER_DOMAIN, RTE, accessToken, deviceType, onToast, setPending]);

  const onAdEndRef = useRef(() => {});
  onAdEndRef.current = async () => {
    if (Date.now() < ignoreAdEventUntilRef.current) {
      return;
    }
    if (claimLockRef.current) {
      return;
    }
    if (errorTimerRef.current) {
      window.clearTimeout(errorTimerRef.current);
      errorTimerRef.current = null;
    }
    const pending = takePending();
    if (!pending?.code) {
      return;
    }
    claimLockRef.current = true;
    try {
      const json = await postClaim({ code: pending.code, segmentNo: pending.segmentNo });
      if (json?.result !== 'ok') {
        onToast?.(json?.message || '보상을 받지 못했어요.');
      } else {
        playClaimBurst(json);
        onToast?.(json.toastMessage);
        if (json.extraToast) {
          onToast?.(json.extraToast);
        }
      }
      await mutate?.();
    } catch (error) {
      console.error('pedometer claim error', error);
      onToast?.('보상을 받지 못했어요.');
    } finally {
      claimLockRef.current = false;
      setBusy(false);
    }
  };

  const listenerParams = useMemo(() => ({ task: 'pedometer', onError: handleAdError }), [handleAdError]);

  useAdsListener('', '', listenerParams, () => {
    onAdEndRef.current();
  });

  /** 광고 없는 구간도 탭 1회에 1칸만 수령한다. */
  const claimOne = useCallback(
    async (segmentNo) => {
      const segment = segmentByNo(segmentNo);
      if (!segment || segment.needsAd || steps < segment.steps) {
        return false;
      }
      const json = await postClaim({ segmentNo });
      if (json?.result !== 'ok') {
        onToast?.(json?.message || '보상을 받지 못했어요.');
        return false;
      }
      playClaimBurst(json);
      onToast?.(json.toastMessage);
      if (json.extraToast) {
        onToast?.(json.extraToast);
      }
      await mutate?.();
      return true;
    },
    [mutate, onToast, playClaimBurst, postClaim, segmentByNo, steps],
  );

  const onSegmentTap = useCallback(
    async (segment) => {
      if (!segment || busy) {
        return;
      }
      if (segment.status === 'DONE') {
        onToast?.('이미 받은 보상이에요');
        return;
      }
      if (segment.status === 'READY') {
        onToast?.('이전 보상을 먼저 받아주세요!');
        return;
      }
      if (segment.status === 'LOCK') {
        const remain = Math.max(0, segment.steps - steps);
        onToast?.(
          remain > 0
            ? `${remain.toLocaleString('ko-KR')}걸음 더 걸으면 열려요`
            : '이전 보상을 먼저 받아주세요!',
        );
        return;
      }

      if (segment.needsAd) {
        await startAd();
        return;
      }

      setBusy(true);
      try {
        await claimOne(segment.segmentNo);
      } finally {
        setBusy(false);
      }
    },
    [busy, claimOne, onToast, startAd, steps],
  );

  return { onSegmentTap, busy, claimBurst };
}
